import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ChefHat, Loader2 } from 'lucide-react';

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [li, setLi] = useState({ email: '', password: '' });
  const [rg, setRg] = useState({ business_name: '', trn: '', name: '', email: '', password: '' });

  const doLogin = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      await login(data.token);
      toast.success('Welcome back!');
      nav('/');
    } catch (e) { toast.error(e.response?.data?.detail || 'Login failed'); }
    finally { setLoading(false); }
  };

  const doRegister = async () => {
    if (!rg.business_name || !rg.name || !rg.email || !rg.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/register', rg);
      await login(data.token);
      toast.success("Account created! Let's set up your restaurant.");
      nav('/onboarding');
    } catch (e) { toast.error(e.response?.data?.detail || 'Sign up failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-sidebar text-sidebar-foreground relative overflow-hidden">
        <div className="absolute inset-0 hero-grain opacity-30" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary flex items-center justify-center"><ChefHat className="h-6 w-6 text-primary-foreground" /></div>
          <span className="font-display font-bold text-2xl">RoboChef</span>
        </div>
        <div className="relative z-10 space-y-4">
          <h1 className="font-display text-4xl font-bold leading-tight">Run your entire restaurant on one platform.</h1>
          <p className="text-sidebar-foreground/70 max-w-md">POS billing, table management, kitchen display, inventory, CRM, loyalty and AI-powered insights — built for Dubai with UAE VAT & AED.</p>
          <div className="flex gap-2 flex-wrap pt-2">
            {['POS & VAT Invoicing', 'Kitchen Display', 'Inventory & Recipes', 'AI Insights', 'Loyalty & CRM'].map((t) => (
              <span key={t} className="text-xs px-3 py-1.5 rounded-full bg-white/10">{t}</span>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-sidebar-foreground/50">Dubai, UAE</div>
      </div>

      <div className="flex items-center justify-center p-6 bg-background">
        <Card className="w-full max-w-md p-6">
          <div className="lg:hidden flex items-center gap-2 mb-4">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center"><ChefHat className="h-5 w-5 text-primary-foreground" /></div>
            <span className="font-display font-bold text-xl">RoboChef</span>
          </div>
          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 w-full mb-4">
              <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-3">
              <div><Label>Email</Label><Input data-testid="login-email" value={li.email} onChange={(e) => setLi({ ...li, email: e.target.value })} placeholder="owner@robochef.com" /></div>
              <div><Label>Password</Label><Input data-testid="login-password" type="password" value={li.password} onChange={(e) => setLi({ ...li, password: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && doLogin(li.email, li.password)} placeholder="••••••••" /></div>
              <Button data-testid="login-submit" className="w-full" disabled={loading} onClick={() => doLogin(li.email, li.password)}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
              </Button>
            </TabsContent>

            <TabsContent value="register" className="space-y-3">
              <div><Label>Business Name *</Label><Input data-testid="reg-business" value={rg.business_name} onChange={(e) => setRg({ ...rg, business_name: e.target.value })} placeholder="RoboChef Foods LLC" /></div>
              <div><Label>TRN (Tax Reg. No.)</Label><Input data-testid="reg-trn" value={rg.trn} onChange={(e) => setRg({ ...rg, trn: e.target.value })} placeholder="100xxxxxxxxxxxx" /></div>
              <div><Label>Your Name *</Label><Input data-testid="reg-name" value={rg.name} onChange={(e) => setRg({ ...rg, name: e.target.value })} placeholder="Full name" /></div>
              <div><Label>Email *</Label><Input data-testid="reg-email" value={rg.email} onChange={(e) => setRg({ ...rg, email: e.target.value })} placeholder="you@example.com" /></div>
              <div><Label>Password *</Label><Input data-testid="reg-password" type="password" value={rg.password} onChange={(e) => setRg({ ...rg, password: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && doRegister()} placeholder="••••••••" /></div>
              <Button data-testid="reg-submit" className="w-full" disabled={loading} onClick={doRegister}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Account'}
              </Button>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}
